import { z } from "zod";

/**
 * Low-cardinality transport telemetry shared by workers, relays, and clients.
 * Identifiers (terminal, job, user, request) intentionally do not belong in
 * dimensions: they make metrics expensive and can leak user-controlled data.
 */
export const TransportMetricName = z.enum([
  "transport.latency.terminal_echo_ms",
  "transport.latency.command_ack_ms",
  "transport.latency.event_display_ms",
  "transport.ws.buffered_bytes",
  "transport.event_loop.delay_ms",
  "transport.spool.depth",
  "transport.spool.oldest_age_ms",
  "transport.reconnect.attempt",
  "transport.replay.gap",
  "transport.replay.snapshot_fallback",
  "transport.drop",
  "transport.preview.credit_bytes",
  "transport.direct.credit_bytes",
  "transport.direct.fallback",
]);
export type TransportMetricName = z.infer<typeof TransportMetricName>;

export const TransportMetricKind = z.enum(["counter", "gauge", "histogram"]);
export type TransportMetricKind = z.infer<typeof TransportMetricKind>;

const MAX_DIMENSIONS = 8;
const Dimensions = z.record(
  z.string().min(1).max(40).regex(/^[a-z][a-z0-9_]*$/),
  z.string().min(1).max(80).regex(/^[A-Za-z0-9_.:-]+$/),
).superRefine((value, context) => {
  if (Object.keys(value).length > MAX_DIMENSIONS) {
    context.addIssue({ code: "custom", message: `at most ${MAX_DIMENSIONS} dimensions are allowed` });
  }
  for (const key of Object.keys(value)) {
    if (key === "id" || key.endsWith("_id") || ["user", "terminal", "job", "request", "repository", "worktree"].includes(key)) {
      context.addIssue({ code: "custom", path: [key], message: "high-cardinality identifiers are not metric dimensions" });
    }
  }
});

const METRIC_KINDS: Readonly<Record<TransportMetricName, TransportMetricKind>> = {
  "transport.latency.terminal_echo_ms": "histogram",
  "transport.latency.command_ack_ms": "histogram",
  "transport.latency.event_display_ms": "histogram",
  "transport.ws.buffered_bytes": "gauge",
  "transport.event_loop.delay_ms": "histogram",
  "transport.spool.depth": "gauge",
  "transport.spool.oldest_age_ms": "gauge",
  "transport.reconnect.attempt": "counter",
  "transport.replay.gap": "counter",
  "transport.replay.snapshot_fallback": "counter",
  "transport.drop": "counter",
  "transport.preview.credit_bytes": "gauge",
  "transport.direct.credit_bytes": "gauge",
  "transport.direct.fallback": "counter",
};

export const TransportMetricRecord = z.object({
  version: z.literal(1),
  name: TransportMetricName,
  kind: TransportMetricKind,
  value: z.number().finite().nonnegative(),
  timestampMs: z.number().int().nonnegative(),
  dimensions: Dimensions.default({}),
}).superRefine((record, context) => {
  const expected = METRIC_KINDS[record.name];
  if (record.kind !== expected) {
    context.addIssue({ code: "custom", path: ["kind"], message: `${record.name} must be a ${expected}` });
  }
});
export type TransportMetricRecord = z.infer<typeof TransportMetricRecord>;

export interface TransportMetricSink {
  emit(record: TransportMetricRecord): void;
}

export interface TransportMetricAggregate {
  count: number;
  sum: number;
  min: number;
  max: number;
  last: number;
  p50?: number;
  p95?: number;
  p99?: number;
}

export interface TransportMetricsReport {
  version: 1;
  generatedAtMs: number;
  metrics: Partial<Record<TransportMetricName, TransportMetricAggregate>>;
}

interface MutableAggregate extends TransportMetricAggregate {
  samples: number[];
  cursor: number;
}

/**
 * A bounded in-process aggregator. The sink is the integration seam for a
 * production metrics backend; quantile samples are capped so instrumentation
 * cannot become an outage amplifier.
 */
export class TransportMetrics implements TransportMetricSink {
  private readonly values = new Map<TransportMetricName, MutableAggregate>();

  constructor(
    private readonly now: () => number = Date.now,
    private readonly sampleLimit = 8_192,
    private readonly downstream?: TransportMetricSink,
  ) {
    if (!Number.isSafeInteger(sampleLimit) || sampleLimit < 1) throw new Error("sampleLimit must be a positive integer");
  }

  observe(name: TransportMetricName, value: number, dimensions: Record<string, string> = {}): TransportMetricRecord {
    return this.record(name, "histogram", value, dimensions);
  }

  gauge(name: TransportMetricName, value: number, dimensions: Record<string, string> = {}): TransportMetricRecord {
    return this.record(name, "gauge", value, dimensions);
  }

  increment(name: TransportMetricName, amount = 1, dimensions: Record<string, string> = {}): TransportMetricRecord {
    return this.record(name, "counter", amount, dimensions);
  }

  emit(record: TransportMetricRecord): void {
    const validated = TransportMetricRecord.parse(record);
    this.accumulate(validated);
    this.downstream?.emit(validated);
  }

  report(): TransportMetricsReport {
    const metrics: Partial<Record<TransportMetricName, TransportMetricAggregate>> = {};
    for (const [name, aggregate] of this.values) {
      const output: TransportMetricAggregate = {
        count: aggregate.count,
        sum: aggregate.sum,
        min: aggregate.min,
        max: aggregate.max,
        last: aggregate.last,
      };
      if (METRIC_KINDS[name] === "histogram") {
        output.p50 = quantile(aggregate.samples, 0.5);
        output.p95 = quantile(aggregate.samples, 0.95);
        output.p99 = quantile(aggregate.samples, 0.99);
      }
      metrics[name] = output;
    }
    return { version: 1, generatedAtMs: this.now(), metrics };
  }

  private record(
    name: TransportMetricName,
    kind: TransportMetricKind,
    value: number,
    dimensions: Record<string, string>,
  ): TransportMetricRecord {
    const record = TransportMetricRecord.parse({
      version: 1,
      name,
      kind,
      value,
      timestampMs: Math.max(0, Math.floor(this.now())),
      dimensions,
    });
    this.accumulate(record);
    this.downstream?.emit(record);
    return record;
  }

  private accumulate(record: TransportMetricRecord): void {
    let aggregate = this.values.get(record.name);
    if (!aggregate) {
      aggregate = {
        count: 0,
        sum: 0,
        min: Number.POSITIVE_INFINITY,
        max: Number.NEGATIVE_INFINITY,
        last: 0,
        samples: [],
        cursor: 0,
      };
      this.values.set(record.name, aggregate);
    }
    aggregate.count += 1;
    aggregate.sum += record.value;
    aggregate.min = Math.min(aggregate.min, record.value);
    aggregate.max = Math.max(aggregate.max, record.value);
    aggregate.last = record.value;
    if (record.kind === "histogram") {
      if (aggregate.samples.length < this.sampleLimit) aggregate.samples.push(record.value);
      else {
        // A deterministic ring keeps memory bounded. Production backends can
        // retain richer histograms through the downstream sink.
        aggregate.samples[aggregate.cursor] = record.value;
        aggregate.cursor = (aggregate.cursor + 1) % this.sampleLimit;
      }
    }
  }
}

export type TransportSloProfile = "local" | "relay";
export type LatencyMetricName = Extract<TransportMetricName, `transport.latency.${string}`>;

export interface QuantileBudgetMs { p50: number; p95: number; p99: number }
export interface TransportSloBudget {
  latencyMs: Record<LatencyMetricName, QuantileBudgetMs>;
  eventLoopP99Ms: number;
  maxWebSocketBufferedBytes: number;
  maxSpoolDepth: number;
  maxSpoolOldestAgeMs: number;
  maxDrops: 0;
}

/** Initial product budgets. Production measurements must be labeled `relay`;
 * chaos simulations are evidence about invariants, not production latency. */
export const TRANSPORT_SLO_POLICY: Readonly<Record<TransportSloProfile, TransportSloBudget>> = {
  local: {
    latencyMs: {
      "transport.latency.terminal_echo_ms": { p50: 20, p95: 50, p99: 100 },
      "transport.latency.command_ack_ms": { p50: 35, p95: 100, p99: 200 },
      "transport.latency.event_display_ms": { p50: 25, p95: 75, p99: 150 },
    },
    eventLoopP99Ms: 20,
    maxWebSocketBufferedBytes: 4 * 1024 * 1024,
    maxSpoolDepth: 20_000,
    maxSpoolOldestAgeMs: 30_000,
    maxDrops: 0,
  },
  relay: {
    latencyMs: {
      "transport.latency.terminal_echo_ms": { p50: 75, p95: 180, p99: 350 },
      "transport.latency.command_ack_ms": { p50: 150, p95: 500, p99: 1_200 },
      "transport.latency.event_display_ms": { p50: 125, p95: 400, p99: 1_000 },
    },
    eventLoopP99Ms: 50,
    maxWebSocketBufferedBytes: 4 * 1024 * 1024,
    maxSpoolDepth: 20_000,
    maxSpoolOldestAgeMs: 5 * 60_000,
    maxDrops: 0,
  },
};

export interface TransportSloResult {
  profile: TransportSloProfile;
  passed: boolean;
  failures: string[];
}

export function evaluateTransportSlo(
  report: TransportMetricsReport,
  profile: TransportSloProfile,
  options: { requireMeasurements?: boolean } = {},
): TransportSloResult {
  const budget = TRANSPORT_SLO_POLICY[profile];
  const failures: string[] = [];
  const required = options.requireMeasurements ?? true;

  for (const [name, limits] of Object.entries(budget.latencyMs) as [LatencyMetricName, QuantileBudgetMs][]) {
    const metric = report.metrics[name];
    if (!metric) {
      if (required) failures.push(`missing ${name}`);
      continue;
    }
    for (const percentile of ["p50", "p95", "p99"] as const) {
      const actual = metric[percentile];
      if (actual === undefined) failures.push(`missing ${name}.${percentile}`);
      else if (actual > limits[percentile]) failures.push(`${name}.${percentile} ${actual}ms > ${limits[percentile]}ms`);
    }
  }

  checkMaximum(report, "transport.event_loop.delay_ms", "p99", budget.eventLoopP99Ms, failures, required);
  checkMaximum(report, "transport.ws.buffered_bytes", "max", budget.maxWebSocketBufferedBytes, failures, required);
  checkMaximum(report, "transport.spool.depth", "max", budget.maxSpoolDepth, failures, required);
  checkMaximum(report, "transport.spool.oldest_age_ms", "max", budget.maxSpoolOldestAgeMs, failures, required);
  const drops = report.metrics["transport.drop"]?.sum ?? 0;
  if (drops > budget.maxDrops) failures.push(`transport.drop ${drops} > ${budget.maxDrops}`);
  for (const name of ["transport.preview.credit_bytes", "transport.direct.credit_bytes"] as const) {
    const metric = report.metrics[name];
    if (required && !metric) failures.push(`missing ${name}`);
    if (metric && metric.min < 0) failures.push(`${name}.min ${metric.min} < 0`);
  }
  return { profile, passed: failures.length === 0, failures };
}

function checkMaximum(
  report: TransportMetricsReport,
  name: TransportMetricName,
  field: "max" | "p99",
  limit: number,
  failures: string[],
  required: boolean,
): void {
  const metric = report.metrics[name];
  if (!metric) {
    if (required) failures.push(`missing ${name}`);
    return;
  }
  const actual = metric[field];
  if (actual === undefined) failures.push(`missing ${name}.${field}`);
  else if (actual > limit) failures.push(`${name}.${field} ${actual} > ${limit}`);
}

function quantile(values: readonly number[], percentile: number): number | undefined {
  if (values.length === 0) return undefined;
  const ordered = [...values].sort((a, b) => a - b);
  const index = Math.max(0, Math.ceil(percentile * ordered.length) - 1);
  return ordered[index];
}
